/**
 * Core Layer - Socket Manager
 * Handles Socket.io connection, events, and message passing
 * No state management, no business logic - pure socket operations
 */

import {io} from "socket.io-clientv4";
import {HMACUtility} from "../../../../Utilities/HMACUtility";

export class SocketManager {
    constructor() {
        this.socket = null;
        this.eventHandlers = new Map();
        this.retryCount = 0;
        this.maxRetries = 5;
        this.baseDelay = 1000;
    }

    /**
     * Connect to socket server
     */
    connect(serverAddress, authToken) {
        if (this.socket) {
            this.disconnect();
        }

        this.socket = io(serverAddress, {
            auth: {token: authToken}
        });

        // Setup default error handlers
        this.socket.on('connect_error', this.handleError.bind(this));
        this.socket.on('disconnect', this.handleError.bind(this));
        this.socket.on('connect', () => {
            this.retryCount = 0;
            console.log('Successfully connected to the socket server.');
        });

        this.socket.connect();

        return this.socket;
    }

    /**
     * Disconnect from socket server
     */
    disconnect() {
        if (this.socket) {
            this.socket.disconnect();
            this.socket = null;
        }
        this.eventHandlers.clear();
    }

    /**
     * Handle socket errors with retry logic
     */
    handleError(error) {
        console.error('Socket error:', error);

        if (this.retryCount < this.maxRetries) {
            const retryDelay = this.baseDelay * Math.pow(2, this.retryCount);
            console.log(`Retrying connection in ${retryDelay / 1000} seconds...`);

            setTimeout(() => {
                this.retryCount++;
                if (this.socket) {
                    this.socket.connect();
                }
            }, retryDelay);
        } else {
            console.error(`Max retries (${this.maxRetries}) reached. Connection failed.`);
        }
    }

    /**
     * Join a room
     */
    joinRoom(roomId) {
        if (this.socket) {
            this.socket.emit('JoinRoom', roomId);
        }
    }

    /**
     * Emit an event
     */
    emit(eventName, data) {
        if (this.socket) {
            this.socket.emit(eventName, data);
        }
    }

    /**
     * Listen to an event
     */
    on(eventName, handler) {
        if (this.socket) {
            this.socket.on(eventName, handler);
            this.eventHandlers.set(eventName, handler);
        }
    }

    /**
     * Remove event listener
     */
    off(eventName) {
        if (this.socket && this.eventHandlers.has(eventName)) {
            const handler = this.eventHandlers.get(eventName);
            this.socket.off(eventName, handler);
            this.eventHandlers.delete(eventName);
        }
    }

    /**
     * Send heartbeat
     */
    sendHeartbeat(roomId, deviceId, micOn, name, userDetails) {
        const data = JSON.stringify({
            roomId,
            MessageIdRef: 22,
            Sender: {
                AvatarBackground: userDetails.AvatarBackground,
                _id: userDetails._id,
                Name: userDetails.Name || name,
                LastName: userDetails.LastName,
                UserIcon: userDetails.UserIcon,
            },
            data: "OnHeartbeat",
            SenderSocketId: deviceId,
            micOn,
        });

        this.emit('OnHeartbeat', data);
    }

    /**
     * Send room message
     */
    sendRoomMessage(roomId, data, senderId) {
        const roomMessage = JSON.stringify({
            roomId,
            data,
            SenderSocketId: senderId,
        });

        this.emit('OnRoomMessage', roomMessage);
    }

    async sendSignedMessage(targetRoomId, payload, secret, senderId) {
        const payloadString = JSON.stringify(payload);
        const signature = await HMACUtility.signMessage(payloadString, secret);
        const envelope = {
            signedContent: payloadString,
            signature: signature
        };
        this.sendRoomMessage(targetRoomId, JSON.stringify(envelope), senderId);
    }

    /**
     * Request keyframe from participant
     */
    requestKeyframe(participantId, deviceId) {
        const videoChannel = `dynamic${participantId}High`;
        this.emit("RequestKeyframe", JSON.stringify({
            roomId: videoChannel,
            SenderSocketId: deviceId
        }));
    }

    /**
     * Notify user left
     */
    notifyUserLeft(roomId, deviceId, callback = () => {
    }) {
        let data = JSON.stringify({
            roomId,
            SenderSocketId: deviceId
        });
        this.emit("OnUserLeft", data, callback);
    }

    /**
     * Notify screen share started
     */
    notifyShareScreenStarted(roomId, deviceId) {
        const roomMessage = JSON.stringify({
            roomId,
            SenderSocketId: deviceId,
        });
        this.emit('OnShareScreenStarted', roomMessage);
    }

    /**
     * Send video data
     */
    sendVideoData(channel, arrayBuffer) {
        this.emit(channel, arrayBuffer);
    }

    /**
     * Send audio data
     */
    sendAudioData(roomId, arrayBuffer) {
        this.emit('DataMessage-' + roomId, arrayBuffer);
    }

    /**
     * Get socket instance (for direct access if needed)
     */
    getSocket() {
        return this.socket;
    }

    /**
     * Check if connected
     */
    isConnected() {
        return this.socket && this.socket.connected;
    }
}
